<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { mdiPhoneForward, mdiClose, mdiPhoneMinus } from '@mdi/js';
import { useDisplay } from 'vuetify';
import ExportButton from '@/components/ExportButton.vue';
import StandardFilterRow from '@/components/StandardFilterRow.vue';
import DidRoutes from './DidRoutes.vue';
import TableEmptyState from '@/components/TableEmptyState.vue';
import TablePagination from '@/components/TablePagination.vue';
import { useDidNumbers } from '@/composables/useDidNumbers';
import { getFlagEmoji, getCountryNameFromCode } from '@/utils/countryUtils';
import { formatRelativeTime, formatDate } from '@/utils/formatters';
import type { ApplyFiltersPayload, FilterConfig } from '@/types';
import type { DidNumber, DidNumberFilters } from '@/services/didService';
import {
  createDateFilter,
  createSelectFilter,
  createNumberFilter
} from '@/utils/filters';

const {
  numbers,
  loading,
  error,
  currentPage,
  itemsPerPage,
  totalItems,
  exporting,
  hasError,
  fetchDidNumbers,
  changePage,
  changeItemsPerPage,
  updateFilters,
  resetFilters,
  exportDidNumbers,
  getSmsStatusColor,
  getSmsStatusDisplay
} = useDidNumbers();

const selectedDid = ref<DidNumber | null>(null);
const routesPanel = ref(false);
const search = ref('');

const { mdAndDown } = useDisplay();

const icons = { mdiPhoneForward, mdiClose };

const filters = ref<FilterConfig[]>([
  createSelectFilter('SMS Status', [
    { text: 'All', value: '' },
    { text: 'Enabled', value: 'true' },
    { text: 'Disabled', value: 'false' }
  ], 'smsEnabled'),
  createNumberFilter('Max Channels', undefined, 'channelRange'),
  createDateFilter('Date Added', 'dateRange'),
]);

const buildDidFilters = (sourceFilters: FilterConfig[]): Partial<DidNumberFilters> => {
  const filterParams: Partial<DidNumberFilters> = {};
  for (const filter of sourceFilters) {
    if (filter.key === 'smsEnabled') {
      const value = filter.value;
      if (Array.isArray(value) && value.length > 0 && value[0] !== '') {
        filterParams.smsEnabled = { value: value[0] };
      }
    } else if (filter.key === 'channelRange') {
      const value = filter.value;
      if (Array.isArray(value) && value.length === 2) {
        filterParams.channelRange = { value: [value[0], value[1]] };
      }
    } else if (filter.key === 'dateRange') {
      const value = filter.value;
      if (Array.isArray(value) && value.length === 2) {
        filterParams.dateRange = { value: [value[0], value[1]] };
      }
    }
  }
  return filterParams;
};

const handleApplyFilters = (payload: ApplyFiltersPayload) => {
  search.value = payload.search;
  filters.value = payload.filters;
  updateFilters({
    ...buildDidFilters(payload.filters),
    search: payload.search
  });
};

const handleReset = () => {
  filters.value.forEach((filter: FilterConfig) => {
    if (filter.type === 'number' || filter.type === 'date') {
      filter.value = [null, null];
    } else if (filter.type === 'select') {
      filter.value = [];
    } else {
      filter.value = null;
    }
  });
  search.value = '';
  resetFilters();
};

const openRoutesPanel = (did: DidNumber) => {
  selectedDid.value = did;
  routesPanel.value = true;
};

const closeRoutesPanel = () => {
  routesPanel.value = false;
  selectedDid.value = null;
};

const onPageChange = (page: number) => {
  changePage(page);
};

const onItemsPerPageChange = (value: number) => {
  changeItemsPerPage(value);
};

const handleExport = async () => {
  await exportDidNumbers();
};

const highlight = (text: string | null | undefined): string => {
  if (!text) return '';
  const escaped = String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
  const q = (search.value || '').trim();
  if (!q) return escaped;
  const safe = q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return escaped.replace(new RegExp(`(${safe})`, 'gi'), '<mark class="search-hit">$1</mark>');
};

onMounted(() => {
  fetchDidNumbers();
});
</script>

<template>
  <div class="page">
    <v-card>
      <v-card-title class="d-flex align-center justify-space-between flex-wrap pa-4">
        <div>
          <div class="table-title">DID Numbers</div>
          <div class="table-subtitle">Phone numbers for inbound calls</div>
        </div>
        <export-button
          :loading="exporting"
          @click="handleExport"
        />
      </v-card-title>

      <v-divider />

      <v-card-text>
        <standard-filter-row
          :search="search"
          :filters="filters"
          search-placeholder="Search numbers..."
          @apply-filters="handleApplyFilters"
          @reset="handleReset"
        />

        <v-alert
          v-if="hasError"
          type="error"
          variant="tonal"
          class="mb-4"
          :text="error || 'An error occurred'"
          closable
        />

        <!-- Loading skeleton -->
        <div v-if="loading && numbers.length === 0" class="loading-skeleton">
          <div v-for="i in 5" :key="i" class="skeleton-row">
            <div class="skeleton-cell skeleton-cell--md"></div>
            <div class="skeleton-cell skeleton-cell--sm"></div>
            <div class="skeleton-cell skeleton-cell--sm"></div>
            <div class="skeleton-cell skeleton-cell--md"></div>
            <div class="skeleton-cell skeleton-cell--sm"></div>
          </div>
        </div>

        <!-- Mobile card view -->
        <div v-else-if="mdAndDown" class="mobile-cards">
          <v-card
            v-for="did in numbers"
            :key="did.id"
            class="mobile-card mb-3"
          >
            <div class="mobile-card__header">
              <div class="flex-grow-1 min-width-0">
                <div class="mobile-card__name">
                  <span
                    v-if="did.countryCode"
                    class="flag-emoji"
                    :title="getCountryNameFromCode(did.countryCode)"
                  >{{ getFlagEmoji(did.countryCode) }}</span>
                  <span v-html="highlight(did.number)"></span>
                </div>
                <div class="mobile-card__desc" v-html="highlight(
                  did.description
                    || (did.countryCode ? getCountryNameFromCode(did.countryCode) : 'No description')
                )"></div>
              </div>
              <v-btn
                :icon="icons.mdiPhoneForward"
                variant="text"
                color="primary"
                size="small"
                :title="'Manage routes'"
                @click="openRoutesPanel(did)"
              />
            </div>

            <v-divider class="my-2" />

            <div class="mobile-card__body">
              <div class="mobile-card__row">
                <span class="mobile-card__label">SMS</span>
                <v-chip
                  :color="getSmsStatusColor(did.isSmsEnabled)"
                  variant="tonal"
                  size="x-small"
                  class="status-chip"
                >
                  {{ getSmsStatusDisplay(did.isSmsEnabled) }}
                </v-chip>
              </div>

              <div class="mobile-card__row">
                <span class="mobile-card__label">Channels</span>
                <span class="cell-primary">{{ did.custMaxChannels || 'Unlimited' }}</span>
              </div>

              <div class="mobile-card__row">
                <span class="mobile-card__label">Rate</span>
                <span class="cell-primary text-mono">
                  {{ did.monthlyRate ? `$${did.monthlyRate.toFixed(2)}/mo` : '—' }}
                </span>
                <span class="text-medium-emphasis mx-2">·</span>
                <span class="cell-secondary text-mono">
                  {{ did.ratePerMin ? `$${did.ratePerMin.toFixed(5)}/min` : '—' }}
                </span>
              </div>

              <div class="mobile-card__row">
                <span class="mobile-card__label">Next billing</span>
                <span v-if="did.nextBillingDate" class="cell-primary text-mono">
                  {{ formatDate(did.nextBillingDate) }}
                </span>
                <span v-else class="cell-secondary">—</span>
              </div>
            </div>
          </v-card>
        </div>

        <!-- Desktop table -->
        <v-data-table
          v-else
          :headers="[
            { title: 'Number', key: 'number', sortable: true, width: '18%' },
            { title: 'SMS', key: 'isSmsEnabled', sortable: true, width: '10%' },
            { title: 'Channels', key: 'custMaxChannels', sortable: true, width: '10%' },
            { title: 'Rate', key: 'ratePerMin', sortable: true, width: '16%', sortRaw: (a, b) => (a.monthlyRate ?? 0) - (b.monthlyRate ?? 0) },
            { title: 'Next billing', key: 'nextBillingDate', sortable: true, width: '14%' },
            { title: 'Added', key: 'dateAdded', sortable: true, width: '14%' },
            { title: '', key: 'actions', sortable: false, width: '64px', align: 'end' }
          ]"
          :items="numbers"
          :loading="loading"
          :items-per-page="itemsPerPage"
          :page="currentPage"
          :total-items="totalItems"
          @update:options="(opts) => {
            if (opts.page !== currentPage) onPageChange(opts.page);
            if (opts.itemsPerPage !== itemsPerPage) onItemsPerPageChange(opts.itemsPerPage);
          }"
          class="did-numbers-table"
          :sort-by="[{ key: 'dateAdded', order: 'desc' }]"
          hover
          fixed-header
        >
          <template v-slot:item.number="{ item }">
            <div class="cell-stack">
              <div class="cell-primary text-mono">
                <span
                  v-if="item.countryCode"
                  class="flag-emoji"
                  :title="getCountryNameFromCode(item.countryCode)"
                >{{ getFlagEmoji(item.countryCode) }}</span>
                <span v-html="highlight(item.number)"></span>
              </div>
              <div
                class="cell-secondary"
                v-html="highlight(
                  item.description
                    || (item.countryCode ? getCountryNameFromCode(item.countryCode) : '')
                )"
              ></div>
            </div>
          </template>

          <template v-slot:item.isSmsEnabled="{ item }">
            <v-chip
              :color="getSmsStatusColor(item.isSmsEnabled)"
              variant="tonal"
              size="small"
              class="status-chip"
            >
              {{ getSmsStatusDisplay(item.isSmsEnabled) }}
            </v-chip>
          </template>

          <template v-slot:item.custMaxChannels="{ item }">
            <span class="cell-primary">{{ item.custMaxChannels || 'Unlimited' }}</span>
          </template>

          <template v-slot:item.ratePerMin="{ item }">
            <div class="cell-stack">
              <span class="cell-primary text-mono">
                {{ item.monthlyRate ? `$${item.monthlyRate.toFixed(2)}/mo` : '—' }}
              </span>
              <span class="cell-secondary text-mono">
                {{ item.ratePerMin ? `$${item.ratePerMin.toFixed(5)}/min` : '—' }}
              </span>
            </div>
          </template>

          <template v-slot:item.nextBillingDate="{ item }">
            <span v-if="item.nextBillingDate" class="cell-primary text-mono">
              {{ formatDate(item.nextBillingDate) }}
            </span>
            <span v-else class="cell-secondary">—</span>
          </template>

          <template v-slot:item.dateAdded="{ item }">
            <v-tooltip
              v-if="item.dateAdded"
              :text="`Added ${formatDate(item.dateAdded)}`"
              location="top"
            >
              <template #activator="{ props: tipProps }">
                <span v-bind="tipProps" class="cell-secondary relative-time">
                  {{ formatRelativeTime(item.dateAdded) }}
                </span>
              </template>
            </v-tooltip>
            <span v-else class="cell-secondary">—</span>
          </template>

          <template v-slot:item.actions="{ item }">
            <div class="d-flex align-center justify-end">
              <v-btn
                :icon="icons.mdiPhoneForward"
                variant="text"
                color="primary"
                size="small"
                :title="'Manage routes for ' + item.number"
                @click="openRoutesPanel(item)"
              />
            </div>
          </template>

          <template v-slot:no-data>
            <table-empty-state
              v-if="!loading"
              title="No DID numbers found"
              message="No DID numbers match your search criteria"
              :icon="mdiPhoneMinus"
            />
          </template>

          <template v-slot:bottom>
            <table-pagination
              :page="currentPage"
              :items-per-page="itemsPerPage"
              :total-items="totalItems"
              :items-per-page-options="[10, 25, 50, 100]"
              @update:page="onPageChange"
              @update:items-per-page="onItemsPerPageChange"
            />
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Routes Management Side Panel -->
    <v-navigation-drawer
      v-model="routesPanel"
      location="right"
      temporary
      :width="mdAndDown ? undefined : 800"
    >
      <template v-slot:prepend>
        <div class="drawer-header">
          <div class="drawer-header__icon">
            <v-icon :icon="icons.mdiPhoneForward" size="20" color="white" />
          </div>
          <div class="flex-grow-1 min-width-0">
            <div class="drawer-header__title">Manage Routes</div>
            <div class="drawer-header__subtitle">{{ selectedDid?.number }}</div>
          </div>
          <v-btn
            :icon="icons.mdiClose"
            variant="text"
            size="small"
            @click="closeRoutesPanel"
          />
        </div>
        <v-divider />
      </template>

      <v-card-text v-if="selectedDid" class="pa-5">
        <DidRoutes
          :did-id="selectedDid.id"
          @update="fetchDidNumbers"
        />
      </v-card-text>
    </v-navigation-drawer>
  </div>
</template>

<style scoped lang="scss">
@use '@/scss/tokens' as *;

.page {
  @include page-container;
}

.table-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: rgb(var(--v-theme-on-surface));
  letter-spacing: -0.01em;
}

.table-subtitle {
  font-size: 0.8125rem;
  color: rgb(var(--v-theme-on-surface-variant));
  margin-top: 2px;
}

.cell-stack {
  display: flex;
  flex-direction: column;
  line-height: 1.3;
  min-width: 0;
}

.cell-primary {
  font-size: 0.875rem;
  font-weight: 500;
  color: rgb(var(--v-theme-on-surface));
}

.cell-secondary {
  font-size: 0.75rem;
  color: rgb(var(--v-theme-lightText));
  margin-top: 2px;
}

.status-chip {
  font-weight: 600;
  font-size: 0.6875rem;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  border-radius: 6px;
  height: 22px;
  padding: 0 8px;
}

.flag-emoji {
  margin-right: 6px;
  font-size: 1.125rem;
  line-height: 1;
}

:deep(.search-hit) {
  background-color: rgba(var(--v-theme-warning), 0.25);
  color: rgb(var(--v-theme-on-surface));
  padding: 0 2px;
  border-radius: 2px;
  font-weight: 600;
}

/* Loading skeleton */
.loading-skeleton {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px 0;
}

.skeleton-row {
  display: flex;
  gap: 16px;
  padding: 14px 16px;
  background: rgb(var(--v-theme-surface-light));
  border-radius: 8px;
  align-items: center;
}

.skeleton-cell {
  height: 14px;
  border-radius: 4px;
  background: linear-gradient(
    90deg,
    rgba(var(--v-theme-on-surface), 0.06) 0%,
    rgba(var(--v-theme-on-surface), 0.12) 50%,
    rgba(var(--v-theme-on-surface), 0.06) 100%
  );
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.4s ease-in-out infinite;

  &--sm { flex: 0 0 80px; }
  &--md { flex: 0 0 140px; }
}

@keyframes skeleton-shimmer {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* Tighter padding for this table */
:deep(.did-numbers-table) {
  .v-data-table__wrapper {
    max-height: calc(100vh - 360px);
    overflow-y: auto;
  }

  th, td {
    padding-inline: 10px !important;
  }
  th {
    padding-block: 10px !important;
  }
  td {
    padding-block: 12px !important;
  }

  thead th {
    position: sticky !important;
    top: 0 !important;
    z-index: 3 !important;
    background-color: rgb(var(--v-theme-surface)) !important;
    box-shadow: inset 0 -1px 0 rgba(var(--v-theme-borderLight), 0.6);
  }
}

/* Drawer */
.drawer-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
}

.drawer-header__icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(135deg, rgb(var(--v-theme-primary)), rgb(var(--v-theme-secondary)));
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 10px -4px rgba(var(--v-theme-primary), 0.4);
}

.drawer-header__title {
  font-size: 1rem;
  font-weight: 600;
  color: rgb(var(--v-theme-on-surface));
  line-height: 1.2;
}

.drawer-header__subtitle {
  font-size: 0.75rem;
  color: rgb(var(--v-theme-lightText));
  margin-top: 2px;
  font-family: 'JetBrains Mono', 'SF Mono', Menlo, Consolas, monospace;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Mobile cards */
.mobile-cards {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.mobile-card {
  padding: 14px 16px;
  border-radius: 12px !important;
}

.mobile-card__header {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.mobile-card__name {
  font-size: 1rem;
  font-weight: 600;
  color: rgb(var(--v-theme-on-surface));
  line-height: 1.3;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.mobile-card__desc {
  font-size: 0.75rem;
  color: rgb(var(--v-theme-lightText));
  margin-top: 4px;
  line-height: 1.4;
}

.mobile-card__body {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.mobile-card__row {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 28px;
  flex-wrap: wrap;
}

.mobile-card__label {
  font-size: 0.75rem;
  color: rgb(var(--v-theme-lightText));
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 500;
  min-width: 80px;
}

.min-width-0 {
  min-width: 0;
}
</style>
